/**
 * The answers someone gives **inside the preview**, and the gate that decides whether a step can
 * be left.
 *
 * This is the front-end half of R16b: to leave step N, every required question of that step has to
 * be answered and within its limits (`isRequired`, `minSelections`/`maxSelections`, `maxLength`).
 * The server deliberately does **not** enforce this on a partial save — someone on step 3 already
 * sent steps 1 and 2 as `IN_PROGRESS`, and refusing those would break draft saving and mis-measure
 * abandonment. It enforces it once, on submit.
 *
 * Which is exactly why it is worth rehearsing here: this rule lives in the client, so the builder
 * is the only place it can be caught being wrong before a real person meets it.
 *
 * Nothing in this module talks to the network. The answers exist while the preview modal is open
 * and are thrown away when it closes — a preview that remembered what was typed would be a ghost
 * draft nobody asked for.
 *
 * @module Pages/BUs/Surveys
 */

import type { SurveyLanguage } from '../Types/Surveys.types';
import type { SurveyOptionDraft, SurveyQuestionDraft } from './surveyForm';

export type PreviewAnswer =
  /** RATING, NPS */
  | { kind: 'number'; value: number }
  /** BOOLEAN */
  | { kind: 'boolean'; value: boolean }
  /** SHORT_TEXT, LONG_TEXT */
  | { kind: 'text'; value: string }
  /** SINGLE_CHOICE, MULTI_CHOICE and each grid row — option uids, in the order picked. */
  | { kind: 'options'; value: string[] };

/** Keyed by question uid, and by `question:row` for each row of a grid. */
export type PreviewAnswers = Record<string, PreviewAnswer | undefined>;

export const rowAnswerKey = (questionUid: string, rowUid: string): string =>
  `${questionUid}:${rowUid}`;

const usableOptions = (question: SurveyQuestionDraft): SurveyOptionDraft[] =>
  question.options.filter(option => option.value.trim() !== '');

const selectedOf = (answer: PreviewAnswer | undefined): string[] =>
  answer?.kind === 'options' ? answer.value : [];

/**
 * Picking an option, with the two rules that make a choice list behave.
 *
 * An **exclusive** option ("nenhuma delas") clears everything else, and picking anything else
 * clears it — otherwise "nenhuma delas, mais três seções" is an answer the results cannot read.
 * A single-choice question replaces rather than adds. Past `maxSelections` the pick is refused,
 * so the limit is felt at the moment it bites instead of at the bottom of the screen.
 */
export const toggleOption = (
  question: SurveyQuestionDraft,
  current: string[],
  optionUid: string,
): string[] => {
  const options = usableOptions(question);
  const option = options.find(entry => entry.uid === optionUid);

  if (!option) return current;

  if (question.type !== 'MULTI_CHOICE') {
    return current.includes(optionUid) ? current : [optionUid];
  }

  if (current.includes(optionUid)) {
    return current.filter(uid => uid !== optionUid);
  }

  if (option.isExclusive) return [optionUid];

  const withoutExclusive = current.filter(uid => {
    const picked = options.find(entry => entry.uid === uid);

    return picked ? !picked.isExclusive : false;
  });

  const max = question.numbers.maxSelections;

  if (max !== undefined && withoutExclusive.length >= max) return withoutExclusive;

  return [...withoutExclusive, optionUid];
};

/** Whether one more option can still be picked — what greys out the remaining boxes at the cap. */
export const isAtSelectionCap = (question: SurveyQuestionDraft, current: string[]): boolean => {
  const max = question.numbers.maxSelections;

  return max !== undefined && current.length >= max;
};

/**
 * Why this question does not let the step advance, or `null` when it does.
 *
 * A grid is never answered itself: each of its rows is, so the parent reports on its rows.
 */
export const questionProblem = (
  question: SurveyQuestionDraft,
  answers: PreviewAnswers,
  language: SurveyLanguage,
): string | null => {
  const label = question.labels[language].trim() || question.key || 'Pergunta sem enunciado';

  if (question.type === 'MATRIX') {
    const missing = question.rows.filter(row => {
      if (!row.isRequired) return false;

      return selectedOf(answers[rowAnswerKey(question.uid, row.uid)]).length === 0;
    });

    if (missing.length === 0) return null;

    return `${label}: falta responder ${missing.length} ${missing.length === 1 ? 'linha' : 'linhas'} da grade.`;
  }

  const answer = answers[question.uid];

  if (question.type === 'SHORT_TEXT' || question.type === 'LONG_TEXT') {
    const text = answer?.kind === 'text' ? answer.value : '';
    const max = question.numbers.maxLength;

    if (max !== undefined && text.length > max) {
      return `${label}: o texto passa de ${max} caracteres.`;
    }

    if (question.isRequired && text.trim() === '') return `${label}: falta responder.`;

    return null;
  }

  if (question.type === 'SINGLE_CHOICE' || question.type === 'MULTI_CHOICE') {
    const selected = selectedOf(answer);

    if (selected.length === 0) {
      return question.isRequired ? `${label}: falta responder.` : null;
    }

    const min = question.numbers.minSelections;
    const max = question.numbers.maxSelections;

    if (question.type === 'MULTI_CHOICE' && min !== undefined && selected.length < min) {
      return `${label}: escolha pelo menos ${min} ${min === 1 ? 'opção' : 'opções'}.`;
    }

    if (question.type === 'MULTI_CHOICE' && max !== undefined && selected.length > max) {
      return `${label}: escolha no máximo ${max} ${max === 1 ? 'opção' : 'opções'}.`;
    }

    return null;
  }

  if (!question.isRequired) return null;

  const answered =
    (answer?.kind === 'number' && Number.isFinite(answer.value)) || answer?.kind === 'boolean';

  return answered ? null : `${label}: falta responder.`;
};

/** Everything holding this step back. Empty means "Continuar" is live. */
export const stepProblems = (
  questions: SurveyQuestionDraft[],
  answers: PreviewAnswers,
  language: SurveyLanguage,
): string[] =>
  questions
    .map(question => questionProblem(question, answers, language))
    .filter((problem): problem is string => problem !== null);
